import { useCallback, type ReactNode } from 'react';
import type { ModalFuncProps } from 'antd';
import StandardDeleteConfirmContent from '@/components/molecules/StandardDeleteConfirmContent';
import {
  STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
  STANDARD_DELETE_CONFIRM_OK_TEXT,
  STANDARD_DELETE_CONFIRM_TITLE,
} from '@/constants/deleteConfirm';
import useAppModal from '@/hooks/useAppModal';

export type OpenStandardDeleteConfirmOptions = {
  onOk: NonNullable<ModalFuncProps['onOk']>;
  okButtonProps?: ModalFuncProps['okButtonProps'];
  cancelButtonProps?: ModalFuncProps['cancelButtonProps'];
  /** Default: 削除確認 */
  title?: string;
  /** Replaces default body (e.g. explicit `StandardDeleteConfirmContent`). */
  content?: ReactNode;
  /** Only used when `content` is omitted; overrides the red main line. */
  messageText?: string;
  /** Only used when `content` is omitted; shown below the main line. */
  extraContent?: ReactNode;
};

/**
 * Opens the standard delete confirmation modal without importing copy constants at call sites.
 * Defaults: title 削除確認, standard red line + optional extra, buttons OK / キャンセル, danger OK.
 *
 * - Typical: `open({ onOk, okButtonProps: { loading } })`
 * - Explicit body: `open({ content: <StandardDeleteConfirmContent />, onOk, ... })`
 * - Custom line only: `open({ messageText: '...', onOk })`
 */
export function useStandardDeleteConfirm() {
  const { confirm } = useAppModal();

  return useCallback(
    (options: OpenStandardDeleteConfirmOptions) => {
      const {
        onOk,
        okButtonProps,
        cancelButtonProps,
        title = STANDARD_DELETE_CONFIRM_TITLE,
        content: contentOverride,
        messageText,
        extraContent,
      } = options;

      const content =
        contentOverride ??
        (messageText !== undefined || extraContent !== undefined ? (
          <StandardDeleteConfirmContent messageText={messageText} extraContent={extraContent} />
        ) : (
          <StandardDeleteConfirmContent />
        ));

      return confirm({
        title,
        content,
        okText: STANDARD_DELETE_CONFIRM_OK_TEXT,
        cancelText: STANDARD_DELETE_CONFIRM_CANCEL_TEXT,
        okButtonProps: { danger: true, ...okButtonProps },
        cancelButtonProps,
        onOk,
      });
    },
    [confirm]
  );
}
